import { Box } from '../../render/parts'
import {
  DESK_HEIGHT,
  Grommet,
  ModestyPanel,
  PanelLegs,
  PrivacyPanel,
  WorkTop,
} from '../parts/worktop'
import type { AssetContext, AssetDefinition } from '../types'

const SIZE = 1.6
/** Main run along the back of the footprint. */
const MAIN_WIDTH = SIZE
const MAIN_DEPTH = 0.8
/** Return coming forward off one end. */
const RETURN_WIDTH = 0.8
/** 4 mm shy of the main top so the two slabs never share a face. */
const RETURN_DEPTH = SIZE - MAIN_DEPTH - 0.004
const MAIN_Z = -SIZE / 2 + MAIN_DEPTH / 2
const RETURN_Z = SIZE / 2 - RETURN_DEPTH / 2
/** Square post under the concave corner, where the two tops meet. */
const POST = 0.07

/**
 * L-shaped workstation: a 1.6 x 0.8 main run with a 0.8 x 0.8 return, both at
 * desk height. Origin at the centre of the 1.6 x 1.6 footprint, y = 0 on the
 * floor, the user sitting in the crook of the L and facing +Z.
 *
 * The frame is deliberately sparse: one end panel at the far end of each run
 * plus a single post at the inner corner, which is what keeps the knee space
 * open on a plan view.
 */
function DeskLShape({ accent, variant = 'derecha' }: AssetContext) {
  // +1 puts the return on the +X side, -1 mirrors the whole thing.
  const side = variant === 'izquierda' ? -1 : 1
  const returnX = side * (SIZE - RETURN_WIDTH) / 2

  return (
    <group>
      <group position={[0, 0, MAIN_Z]}>
        <WorkTop width={MAIN_WIDTH} depth={MAIN_DEPTH} />
      </group>
      <group position={[returnX, 0, RETURN_Z]}>
        <WorkTop width={RETURN_WIDTH} depth={RETURN_DEPTH} />
      </group>

      {/* End panel at the far end of the main run, opposite the return. */}
      <group position={[0, 0, MAIN_Z]}>
        <PanelLegs
          width={MAIN_WIDTH}
          depth={MAIN_DEPTH}
          sides={side === 1 ? 'left' : 'right'}
        />
      </group>
      {/* End panel across the front of the return: same part, turned a quarter turn. */}
      <group position={[returnX, 0, RETURN_Z]} rotation={[0, Math.PI / 2, 0]}>
        <PanelLegs width={RETURN_DEPTH} depth={RETURN_WIDTH} sides="left" />
      </group>
      {/* Inner-corner post, buried 4 mm into the top so no faces are coplanar. */}
      <Box
        tone="shell"
        size={[POST, DESK_HEIGHT - 0.024, POST]}
        position={[side * 0.055, (DESK_HEIGHT - 0.024) / 2, -0.055]}
      />

      <ModestyPanel width={1.2} z={-SIZE / 2 + 0.09} />
      <Grommet x={side * 0.6} z={-SIZE / 2 + 0.2} />

      <PrivacyPanel width={MAIN_WIDTH} accent={accent} z={-SIZE / 2 + 0.03} />
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'desk-l-shape',
  name: 'Escritorio en L',
  category: 'desks',
  footprint: [SIZE, SIZE],
  height: DESK_HEIGHT + 0.41,
  surfaceHeight: DESK_HEIGHT,
  seats: 1,
  occupantSide: 'front',
  accentable: true,
  variants: [
    { id: 'izquierda', name: 'Retorno izquierdo' },
    { id: 'derecha', name: 'Retorno derecho' },
  ],
  keywords: [
    'escritorio',
    'en l',
    'retorno',
    'esquina',
    'l shape',
    'corner desk',
    'return',
    'workstation',
  ],
  Component: DeskLShape,
}
